import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import gqlMacro from 'graphql-tag.macro';
import { useMemo } from 'react';

import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { groupBy } from '@sorare/core/src/lib/arrays';

import {
  useHits,
  useSortAndFilterItems,
} from '#src/hooks/search/useSortedItems.ts';
import type { CardResultsProps } from '#src/searchCards/AdvancedCardSearch/types.ts';
import { CardPreviewGrid } from '#src/searchCards/CardPreviewGrid/index.tsx';

import { Pagination } from '../Pagination/index.tsx';
import type {
  CardResultsFromGraphQL_card,
  CardsQuery,
  CardsQueryVariables,
} from './__generated__/index.graphql.ts';

type CardsQuery_card = CardsQuery['anyCards'][number];

const cardFragment = gql`
  fragment CardResultsFromGraphQL_card on AnyCardInterface {
    slug
    ...CardPreviewGrid_card
  }
  ${CardPreviewGrid.fragments.card}
` as TypedDocumentNode<CardResultsFromGraphQL_card>;

const CARDS_QUERY = gqlMacro`
  query CardsQuery(
    $slugs: [String!]!
    $editableLists: Boolean!
    $onlyPrimary: Boolean!
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
    $withCollectionPredictions: Boolean = false
  ) {
    anyCards(slugs: $slugs) {
      slug
      ...CardResultsFromGraphQL_card
    }
  }
  ${cardFragment}
` as TypedDocumentNode<CardsQuery, CardsQueryVariables>;

export const CardResultsFromGraphQL = (props: CardResultsProps) => {
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();

  const {
    hideOwner,
    galleryOwnerSlug,
    hideSorareUser,
    topic,
    showDesktopFilter,
    onlyPrimary,
    editableLists,
    stackableTypes,
    removeFinishedAuctions,
    removeEndedSingleSaleOffers,
    removeFinishedPrimaryBuys,
    sport,
    selectionMode,
    setSelectionMode,
    totalXpNeeded,
    canDrop,
    canLevelUp,
    hideScoutShortcuts,
    onToggleCard,
    PrependedItemComponent,
    selectedCardSlugs,
    showActionButtonOnFirstCard,
  } = props;
  const {
    loading: hitsLoading,
    hits,
    results,
  } = useHits({ sport, stackableTypes });

  const slugs = useMemo(() => hits?.map(h => h.slug), [hits]);
  const { data, loading: dataLoading } = useQuery(
    CARDS_QUERY,
    {
      variables: {
        slugs: slugs || [],
        editableLists: Boolean(editableLists),
        onlyPrimary: Boolean(onlyPrimary),
        isIosApp,
        isAndroidApp,
        googlePlayStoreCountryCode,
      },
      skip: hitsLoading || !slugs || slugs.length === 0,
    },
    { skipCacheAndNetworkLoadingOverride: true }
  );

  const cardsBySlug = useMemo(
    () =>
      hitsLoading || dataLoading
        ? undefined
        : groupBy(data?.anyCards || [], c => c.slug),
    [data?.anyCards, dataLoading, hitsLoading]
  );

  const { items, loading } = useSortAndFilterItems<CardsQuery_card>({
    hits,
    results,
    cardsBySlug,
    stackableTypes,
    removeFinishedAuctions,
    removeEndedSingleSaleOffers,
    removeFinishedPrimaryBuys,
    hideInstantBuy: false,
    sport,
  });

  return (
    <CardPreviewGrid
      items={items}
      hideOwner={hideOwner}
      galleryOwnerSlug={galleryOwnerSlug}
      topic={topic}
      hideSorareUser={hideSorareUser}
      loading={loading || dataLoading || hitsLoading}
      showDesktopFilter={showDesktopFilter}
      editableLists={editableLists}
      onlyPrimary={!!onlyPrimary}
      sport={sport}
      selectionMode={selectionMode}
      setSelectionMode={setSelectionMode}
      totalXpNeeded={totalXpNeeded}
      hideScoutShortcuts={hideScoutShortcuts}
      canDrop={canDrop}
      canLevelUp={canLevelUp}
      onToggleCard={onToggleCard}
      selectedCardSlugs={selectedCardSlugs}
      showActionButtonOnFirstCard={showActionButtonOnFirstCard}
      prependedItem={
        PrependedItemComponent ? (
          <PrependedItemComponent total={results?.nbHits} />
        ) : undefined
      }
      pagination={<Pagination visible={!!items?.length} />}
    />
  );
};

CardResultsFromGraphQL.fragments = {
  card: cardFragment,
};
